import { createRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { SettingsTable } from "../components/cockpit/settings/SettingsTable";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useCollaborators, useRouteMutations, useRoutes, type RouteConfig } from "../queries";
import { rootRoute } from "./__root";

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/routes",
  component: RoutesScreen,
});

function RoutesScreen() {
  return (
    <ProtectedRoute requiredRoles={["owner", "gm"]}>
      <RoutesContent />
    </ProtectedRoute>
  );
}

function RoutesContent() {
  const routes = useRoutes();
  const collaborators = useCollaborators();
  const mutations = useRouteMutations();
  const [editing, setEditing] = useState<Partial<RouteConfig> | null>(null);

  return (
    <section className="space-y-5">
      <SettingsHeader title="Routes" subtitle="Map Vonigo and custom routes to primary collaborators." />
      <SettingsTable
        title={routes.isLoading ? "Routes loading" : "Routes"}
        rows={routes.data?.items || []}
        addLabel="Add route"
        columns={[
          { key: "code", label: "Code", render: (row) => <span className="font-medium text-slate-950">{row.code}</span> },
          { key: "name", label: "Name", render: (row) => row.name },
          { key: "source", label: "Source", render: (row) => <Badge variant="slate">{row.source}</Badge> },
          { key: "type", label: "Type", render: (row) => <TypeBadge row={row} /> },
          { key: "territory", label: "Territory", render: (row) => row.territory || "-" },
          { key: "primary", label: "Primary", render: (row) => row.primary_collaborator_name || "-" },
          { key: "status", label: "Status", render: (row) => row.status },
        ]}
        onAdd={() => setEditing({ source: "custom", status: "active" })}
        onEdit={setEditing}
        onDeactivate={(row) => mutations.remove.mutate(row.id)}
      />
      {editing ? (
        <RouteForm
          value={editing}
          collaborators={collaborators.data?.items || []}
          saving={mutations.create.isPending || mutations.update.isPending}
          onClose={() => setEditing(null)}
          onSave={(payload) => {
            if (editing.id) {
              mutations.update.mutate({ id: editing.id, payload }, { onSuccess: () => setEditing(null) });
            } else {
              mutations.create.mutate(payload, { onSuccess: () => setEditing(null) });
            }
          }}
        />
      ) : null}
    </section>
  );
}

function RouteForm({
  value,
  collaborators,
  saving,
  onClose,
  onSave,
}: {
  value: Partial<RouteConfig>;
  collaborators: { id: number; name: string }[];
  saving: boolean;
  onClose: () => void;
  onSave: (value: Partial<RouteConfig>) => void;
}) {
  const [form, setForm] = useState(value);
  return (
    <Drawer title={value.id ? "Edit route" : "Add route"} onClose={onClose}>
      <Field label="Code"><Input value={form.code || ""} onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} /></Field>
      <Field label="Name"><Input value={form.name || ""} onChange={(event) => setForm({ ...form, name: event.target.value })} /></Field>
      <Field label="Source">
        <select className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={form.source || "custom"} onChange={(event) => setForm({ ...form, source: event.target.value as RouteConfig["source"] })}>
          <option value="vonigo">vonigo</option>
          <option value="custom">custom</option>
        </select>
      </Field>
      <Field label="Route type">
        <select className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={form.type || ""} onChange={(event) => setForm({ ...form, type: event.target.value ? event.target.value as RouteConfig["type"] : null })}>
          <option value="">Pending classification</option>
          <option value="tech">tech</option>
          <option value="sales">sales</option>
          <option value="virtual">virtual</option>
        </select>
      </Field>
      <Field label="Status">
        <select className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={form.status || "active"} onChange={(event) => setForm({ ...form, status: event.target.value as RouteConfig["status"] })}>
          <option value="active">active</option>
          <option value="paused">paused</option>
          <option value="inactive">inactive</option>
        </select>
      </Field>
      <Field label="Territory"><Input value={form.territory || ""} onChange={(event) => setForm({ ...form, territory: event.target.value })} /></Field>
      <Field label="Vonigo route ID"><Input value={form.vonigo_route_id || ""} onChange={(event) => setForm({ ...form, vonigo_route_id: event.target.value ? Number(event.target.value) : null })} /></Field>
      <Field label="Primary collaborator">
        <select className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm" value={form.primary_collaborator_id || ""} onChange={(event) => setForm({ ...form, primary_collaborator_id: event.target.value ? Number(event.target.value) : null })}>
          <option value="">Unassigned</option>
          {collaborators.map((person) => <option key={person.id} value={person.id}>{person.name}</option>)}
        </select>
      </Field>
      <Button className="w-full" disabled={saving} onClick={() => onSave(form)}>Save route</Button>
    </Drawer>
  );
}

function TypeBadge({ row }: { row: RouteConfig }) {
  if (row.needs_classification) {
    return <Badge variant="amber">Pending classification</Badge>;
  }
  return <Badge variant="green">{row.type}</Badge>;
}

function SettingsHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <Badge variant="slate">Settings</Badge>
      <h1 className="mt-3 text-2xl font-semibold text-slate-950">{title}</h1>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block"><span className="text-xs font-medium text-slate-600">{label}</span><div className="mt-1">{children}</div></label>;
}

function Drawer({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/30">
      <div className="h-full w-full max-w-md space-y-4 overflow-y-auto bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-950">{title}</h3>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
        {children}
      </div>
    </div>
  );
}
